import { useClubAdminGetBillingStatusQuery } from '../../features/SpeechAgendaApiSlice.ts'
import { useBillingRedirect } from '../../app/hooks/UseBillingRedirect.ts'
import LoadingSpinner from '../icons/LoadingSpinner.tsx'
import { BillingStatusDto } from '../../generated'

interface ClubBillingProps {
  clubId: string
}

interface BillingView {
  badge: string
  badgeClass: string
  description: string
  action?: 'subscribe' | 'manage'
  actionLabel?: string
}

const formatEpochDate = (epochSeconds?: number): string | null => {
  if (!epochSeconds) return null
  return new Date(epochSeconds * 1000).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

const buildView = (billing: BillingStatusDto): BillingView => {
  if (billing.billingExempt) {
    return {
      badge: 'Exempt',
      badgeClass: 'bg-green-100 text-green-800',
      description:
        'This club is exempt from billing. No subscription is needed',
    }
  }

  const trialEnd = formatEpochDate(billing.trialEnd)
  const periodEnd = formatEpochDate(billing.currentPeriodEnd)

  if (!billing.subscriptionStatus) {
    return {
      badge: 'No subscription',
      badgeClass: 'bg-amber-100 text-amber-800',
      description:
        'The club is read-only until a subscription starts. Try Speech Agenda free for 90 days - $19/month per club after the trial, no card required to start, cancel anytime',
      action: 'subscribe',
      actionLabel: 'Start your free trial',
    }
  }

  switch (billing.subscriptionStatus) {
    case 'trialing':
      return {
        badge: 'Free trial',
        badgeClass: 'bg-indigo-100 text-indigo-800',
        description: `Your free trial ends${trialEnd ? ` on ${trialEnd}` : ' soon'}. Add a card before then to keep access - $19/month per club. Cancel anytime`,
        action: 'manage',
        actionLabel: 'Manage subscription',
      }
    case 'active':
      return {
        badge: 'Active',
        badgeClass: 'bg-green-100 text-green-800',
        description: `Your subscription is active${periodEnd ? `. Next payment on ${periodEnd}` : ''}`,
        action: 'manage',
        actionLabel: 'Manage subscription',
      }
    case 'canceled':
      return {
        badge: 'Canceled',
        badgeClass: 'bg-gray-100 text-gray-800',
        description:
          'The subscription was canceled, so the club is read-only. Resubscribe to restore full access - resubscriptions are billed immediately at $19/month',
        action: 'subscribe',
        actionLabel: 'Resubscribe',
      }
    default:
      // past_due, unpaid, incomplete, … — anything Stripe considers not in
      // good standing is fixed inside the Stripe-hosted portal.
      return {
        badge: 'Payment issue',
        badgeClass: 'bg-red-100 text-red-800',
        description:
          'There is a problem with the club’s payment, so the club is read-only. Update the payment method to restore full access',
        action: 'manage',
        actionLabel: 'Fix payment',
      }
  }
}

const ClubBilling = ({ clubId }: ClubBillingProps) => {
  const {
    data: billing,
    isLoading,
    isError,
  } = useClubAdminGetBillingStatusQuery(clubId, { skip: !clubId })

  const { subscribe, manageSubscription, isBusy, error } =
    useBillingRedirect(clubId)

  if (isLoading) {
    return (
      <div className="flex justify-center mt-10">
        <LoadingSpinner message="Loading billing details..." />
      </div>
    )
  }

  if (isError || !billing) {
    return (
      <p className="text-sm text-red-600">
        Could not load billing details. Please refresh and try again
      </p>
    )
  }

  const view = buildView(billing)

  return (
    <section className="max-w-2xl">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Subscription</h2>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${view.badgeClass}`}>
            {view.badge}
          </span>
        </div>

        <p className="mt-3 text-sm text-gray-600">{view.description}</p>

        {view.action && (
          <div className="mt-6">
            <button
              type="button"
              disabled={isBusy}
              onClick={() =>
                void (view.action === 'subscribe'
                  ? subscribe()
                  : manageSubscription())
              }
              className="rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60">
              {isBusy ? 'Redirecting…' : view.actionLabel}
            </button>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </div>
        )}

        {view.action === 'manage' && (
          <p className="mt-4 text-xs text-gray-500">
            Payment methods, invoices, and cancellation are handled securely in
            the Stripe customer portal
          </p>
        )}
      </div>

      <p className="mt-4 text-xs text-gray-500">
        Speech Agenda costs $19/month per club after a 90-day free trial
      </p>
    </section>
  )
}

export default ClubBilling
